import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { roleService } from '../../services/roleService'
import { Role } from '../../types/auth'
import RoleFormModal from '../../components/modals/RoleFormModal'
import { 
  Plus, 
  Shield, 
  Edit3, 
  Trash2, 
  Check,
  X,
  Eye
} from 'lucide-react'
import toast from 'react-hot-toast'

const RolesPage: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [editingRole, setEditingRole] = useState<Role | null>(null)
  const queryClient = useQueryClient()

  const { data: roles, isLoading, error } = useQuery({
    queryKey: ['roles'],
    queryFn: roleService.getRoles
  })

  const deleteRoleMutation = useMutation({
    mutationFn: roleService.deleteRole,
    onSuccess: () => {
      queryClient.invalidateQueries(['roles'])
      toast.success('Rol eliminado exitosamente')
    },
    onError: () => {
      toast.error('Error al eliminar rol')
    }
  })

  const handleDeleteRole = (role: Role) => {
    if (window.confirm(`¿Estás seguro de eliminar el rol ${role.name}?`)) {
      deleteRoleMutation.mutate(role.id)
    }
  }

  const getPermissionColor = (hasPermission: boolean) => {
    return hasPermission 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800'
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error al cargar roles</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Roles</h1>
          <p className="mt-2 text-sm text-gray-600">
            Administra roles y permisos del sistema
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary-static btn-md"
        >
          <Plus className="h-5 w-5" />
          Nuevo Rol
        </button>
      </div>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : (
          roles?.map((role) => (
            <div key={role.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              {/* Role Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Shield className="h-6 w-6 text-primary-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">{role.name}</h3>
                    <p className="text-sm text-gray-500">{role.description}</p>
                  </div>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  role.isActive
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {role.isActive ? 'Activo' : 'Inactivo'}
                </span>
              </div>

              {/* Permissions Summary */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Permisos:</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {role.permissions?.all ? (
                    <span className="inline-flex items-center px-2 py-1 rounded bg-green-100 text-green-800">
                      <Check className="h-3 w-3 mr-1" />
                      Todos los permisos
                    </span>
                  ) : (
                    <>
                      <span className={`inline-flex items-center px-2 py-1 rounded ${getPermissionColor(role.permissions?.users?.read)}`}>
                        {role.permissions?.users?.read ? <Check className="h-3 w-3 mr-1" /> : <X className="h-3 w-3 mr-1" />}
                        Usuarios
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded ${getPermissionColor(role.permissions?.roles?.read)}`}>
                        {role.permissions?.roles?.read ? <Check className="h-3 w-3 mr-1" /> : <X className="h-3 w-3 mr-1" />}
                        Roles
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded ${getPermissionColor(role.permissions?.menus?.read)}`}>
                        {role.permissions?.menus?.read ? <Check className="h-3 w-3 mr-1" /> : <X className="h-3 w-3 mr-1" />}
                        Menús
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded ${getPermissionColor(role.permissions?.cruds?.read)}`}>
                        {role.permissions?.cruds?.read ? <Check className="h-3 w-3 mr-1" /> : <X className="h-3 w-3 mr-1" />}
                        CRUDs
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  Creado: {new Date(role.createdAt).toLocaleDateString()}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedRole(role)}
                    className="text-blue-600 hover:text-blue-900 p-1"
                    title="Ver detalles"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setEditingRole(role)}
                    className="text-gray-600 hover:text-gray-900 p-1"
                    title="Editar"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteRole(role)}
                    className="text-red-600 hover:text-red-900 p-1"
                    title="Eliminar"
                    disabled={role.name === 'Admin'} // Prevenir eliminación del rol Admin
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Role Details Modal */}
      {selectedRole && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-96 overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">Detalles del Rol: {selectedRole.name}</h2>
            
            <div className="space-y-4">
              <div>
                <span className="font-medium">Descripción:</span> {selectedRole.description}
              </div>
              
              <div>
                <span className="font-medium">Estado:</span> {selectedRole.isActive ? 'Activo' : 'Inactivo'}
              </div>

              <div>
                <span className="font-medium">Permisos:</span>
                <div className="mt-2 bg-gray-50 p-4 rounded-lg">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                    {JSON.stringify(selectedRole.permissions, null, 2)}
                  </pre>
                </div>
              </div>

              <div>
                <span className="font-medium">Fecha de creación:</span> {new Date(selectedRole.createdAt).toLocaleString()}
              </div>
              
              <div>
                <span className="font-medium">Última actualización:</span> {new Date(selectedRole.updatedAt).toLocaleString()}
              </div>
            </div>

            <button
              onClick={() => setSelectedRole(null)}
              className="btn btn-secondary-static btn-md w-full mt-6"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* Role Form Modals */}
      <RoleFormModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        role={null}
      />
      
      <RoleFormModal
        isOpen={!!editingRole}
        onClose={() => setEditingRole(null)}
        role={editingRole}
      />
    </div>
  )
}

export default RolesPage
